import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Save, Upload } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { itemService } from '@/services/itemService'
import { itemCategoriesService } from '@/services/itemCategoriesService'
import { showSaveSuccess } from '@/lib/swal'
import type { ItemLookupOption, ItemUpsertPayload } from '@/api/types'

type FormState = Omit<ItemUpsertPayload, 'attachmentFile'> & { attachmentFile: File | null }

export const InventoryItemEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId
  const userId = user?.userId || 0
  const isNew = !id || id === 'new'
  const numericId = Number(id || 0)
  const isEditMode = !isNew && Number.isFinite(numericId) && numericId > 0

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState<ItemLookupOption[]>([])
  const [subCategories, setSubCategories] = useState<ItemLookupOption[]>([])
  const [materialTypes, setMaterialTypes] = useState<ItemLookupOption[]>([])
  const [uomOptions, setUomOptions] = useState<ItemLookupOption[]>([])
  const [purchaseTaxes, setPurchaseTaxes] = useState<ItemLookupOption[]>([])
  const [salesTaxes, setSalesTaxes] = useState<ItemLookupOption[]>([])
  const [imagePreviewUrl, setImagePreviewUrl] = useState('')
  const imageInputRef = useRef<HTMLInputElement | null>(null)
  const [form, setForm] = useState<FormState>({
    itemId: 0,
    itemNameEn: '',
    itemNameAr: '',
    itemCode: '',
    reference: '',
    itemCategoryId: 0,
    sku: '',
    barcode: '',
    tags: '',
    shelfId: '',
    description: '',
    subCategoryId: 0,
    uom: 0,
    type: 0,
    costMethod: 0,
    isTaxable: false,
    unitSellingPrice: 0,
    unitCostPrice: 0,
    tax: 0,
    sellingTax: 0,
    attachmentFile: null,
    createdDate: undefined,
    createdBy: undefined,
    modifiedDate: undefined,
    modifiedBy: undefined,
    branchId: branchId || 0,
  })

  useEffect(() => {
    setForm((prev) => ({ ...prev, branchId: branchId || 0 }))
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [cats, types, uoms, purchaseTaxLookups, salesTaxLookups] = await Promise.all([
          itemService.getCategories(branchId),
          itemService.getMaterialTypesLookups(),
          itemService.getMaterialUOMsLookups(),
          itemService.getPurchaseTaxes(branchId),
          itemService.getSalesTaxes(branchId),
        ])
        setCategories(cats)
        setMaterialTypes(types)
        setUomOptions(uoms)
        setPurchaseTaxes(purchaseTaxLookups || [])
        setSalesTaxes(salesTaxLookups || [])
      } catch {
        setCategories([])
        setSubCategories([])
        setMaterialTypes([])
        setUomOptions([])
        setPurchaseTaxes([])
        setSalesTaxes([])
      }
    }
    void loadLookups()
  }, [branchId])

  // Track the friendly label of the saved sub-category so it can still be
  // rendered in the dropdown as a fallback if the loaded list is missing it
  // (e.g. soft-deleted in master, item is from another branch, etc.).
  const [savedSubCategoryLabel, setSavedSubCategoryLabel] = useState('')

  // Fetch sub-categories whenever the selected category changes.
  // IMPORTANT: this effect does NOT touch form.subCategoryId so the saved
  // value is preserved through the async loading window.
  useEffect(() => {
    let cancelled = false
    const loadSubCategoriesByCategory = async () => {
      if (!form.itemCategoryId) {
        setSubCategories([])
        return
      }
      try {
        const subs = await itemCategoriesService.getSubCategoriesByCategoryId(form.itemCategoryId)
        if (cancelled) return
        const mapped: ItemLookupOption[] = subs
          .map((x) => ({
            id: Number(x.id) || 0,
            nameEn: x.nameEn || x.name,
            nameAr: x.nameAr || x.name,
          }))
          .filter((x) => x.id > 0)
        console.log('[InventoryItemEdit] subCategories loaded for category', form.itemCategoryId, {
          raw: subs,
          mapped,
          savedSubCategoryId: form.subCategoryId,
        })
        setSubCategories(mapped)
      } catch (err) {
        if (cancelled) return
        console.warn('[InventoryItemEdit] failed to load sub-categories', err)
        setSubCategories([])
      }
    }
    void loadSubCategoriesByCategory()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, form.itemCategoryId])

  useEffect(() => {
    if (!branchId || !isEditMode) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await itemService.getItemById(numericId, branchId)
        console.log('[InventoryItemEdit] loaded item from server', {
          itemId: data.itemId,
          itemCategoryId: data.itemCategoryId,
          subCategoryId: data.subCategoryId,
          raw: data,
        })

        // Pre-load the sub-category list for the item's category BEFORE
        // setting the form, so the dropdown has the matching option ready
        // when React renders with the saved subCategoryId.
        if (data.itemCategoryId) {
          try {
            const subs = await itemCategoriesService.getSubCategoriesByCategoryId(data.itemCategoryId)
            const mapped: ItemLookupOption[] = subs
              .map((x) => ({
                id: Number(x.id) || 0,
                nameEn: x.nameEn || x.name,
                nameAr: x.nameAr || x.name,
              }))
              .filter((x) => x.id > 0)
            console.log('[InventoryItemEdit] pre-loaded subCategories', {
              forCategory: data.itemCategoryId,
              savedSubCategoryId: data.subCategoryId,
              mapped,
            })
            setSubCategories(mapped)
            // Capture a friendly label for the saved sub-category in case it
            // is missing from the loaded list (broken FK / soft-deleted).
            const hit = mapped.find((x) => x.id === Number(data.subCategoryId || 0))
            if (hit) {
              setSavedSubCategoryLabel(
                language === 'ar' ? hit.nameAr || hit.nameEn : hit.nameEn || hit.nameAr
              )
            } else {
              setSavedSubCategoryLabel('')
            }
          } catch (err) {
            console.warn('[InventoryItemEdit] failed to pre-load sub-categories', err)
          }
        }

        setForm({
          itemId: data.itemId,
          itemNameEn: data.itemNameEn || '',
          itemNameAr: data.itemNameAr || '',
          itemCode: data.itemCode || '',
          reference: data.reference || '',
          itemCategoryId: data.itemCategoryId || 0,
          sku: data.sku || '',
          barcode: data.barcode || '',
          tags: data.tags || '',
          shelfId: data.shelfId || '',
          description: data.description || '',
          subCategoryId: data.subCategoryId || 0,
          uom: data.uom || 0,
          type: data.type || 0,
          costMethod: data.costMethod || 0,
          isTaxable: data.isTaxable,
          unitSellingPrice: data.unitSellingPrice || 0,
          unitCostPrice: data.unitCostPrice || 0,
          tax: data.tax || 0,
          sellingTax: data.sellingTax || 0,
          attachmentFile: null,
          createdDate: data.createdDate,
          createdBy: data.createdBy,
          modifiedDate: data.modifiedDate,
          modifiedBy: data.modifiedBy,
          branchId: data.branchId || branchId,
        })
        setImagePreviewUrl(data.attachmentUrl ?? '')
      } catch (error: any) {
        toast.error(error.response?.data?.message || t('inventoryItems.failedToLoadOne'))
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, isEditMode, numericId, t, language])

  const categoryOptions = useMemo(
    () =>
      categories.map((x) => ({
        value: String(x.id),
        label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
      })),
    [categories, language]
  )

  const subCategoryOptions = useMemo(() => {
    const base = subCategories.map((x) => ({
      value: String(x.id),
      label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
    }))
    // If the form has a sub-category that isn't in the loaded list (e.g.
    // the option list arrived late, FK is broken, or item is from another
    // branch), still expose it so the <Select> can render the value.
    const currentId = Number(form.subCategoryId || 0)
    if (currentId > 0 && !base.some((o) => o.value === String(currentId))) {
      base.unshift({
        value: String(currentId),
        label: savedSubCategoryLabel || String(currentId),
      })
    }
    return base
  }, [subCategories, language, form.subCategoryId, savedSubCategoryLabel])

  const materialTypeOptions = useMemo(
    () =>
      materialTypes.map((x) => ({
        value: String(x.id),
        label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
      })),
    [materialTypes, language]
  )

  const isProcurementLikeLabel = (label: string) => {
    const s = String(label || '').toLowerCase().trim()
    if (!s) return false

    // Tolerant matching for common spellings/typos and Arabic labels
    return (
      s.includes('procure') ||
      s.includes('procur') ||
      s.includes('procurem') ||
      s.includes('procurment') ||
      s.includes('procrement') ||
      s.includes('purchase') ||
      s.includes('purchas') ||
      s.includes('مشتري') ||
      s.includes('مشتريات')
    )
  }

  const procurementTypeId = useMemo(() => {
    const hit = materialTypes.find((x) => {
      const en = String(x.nameEn || '').toLowerCase()
      const ar = String(x.nameAr || '').toLowerCase()
      return isProcurementLikeLabel(en) || isProcurementLikeLabel(ar)
    })
    return hit?.id ?? 0
  }, [materialTypes])

  const isProcurementType = useMemo(() => {
    if (procurementTypeId > 0) return Number(form.type) === procurementTypeId
    const selected = materialTypeOptions.find((x) => x.value === String(form.type || 0))
    return isProcurementLikeLabel(String(selected?.label || ''))
  }, [form.type, materialTypeOptions, procurementTypeId])

  const PROCUREMENT_PURCHASE_TAX_ID = 9
  const PROCUREMENT_SELLING_TAX_ID = 13

  useEffect(() => {
    if (!isProcurementType) return
    setForm((p) => ({
      ...p,
      tax: PROCUREMENT_PURCHASE_TAX_ID,
      sellingTax: PROCUREMENT_SELLING_TAX_ID,
      isTaxable: false,
    }))
  }, [isProcurementType])

  const uomSelectOptions = useMemo(
    () =>
      uomOptions.map((x) => ({
        value: String(x.id),
        label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
      })),
    [uomOptions, language]
  )

  const salesTaxOptions = useMemo(() => {
    return salesTaxes.map((x) => ({
      value: String(x.id),
      label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
    }))
  }, [salesTaxes, language])

  const purchaseTaxOptions = useMemo(() => {
    return purchaseTaxes.map((x) => ({
      value: String(x.id),
      label: language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr,
    }))
  }, [purchaseTaxes, language])

  const handleBack = () => navigate('/inventory/items')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('[InventoryItemEdit] submit clicked', {
      isEditMode,
      branchId,
      itemNameEn: form.itemNameEn,
      itemNameAr: form.itemNameAr,
      itemCategoryId: form.itemCategoryId,
    })
    if (!branchId) {
      console.warn('[InventoryItemEdit] submit blocked: branch not selected')
      toast.error(t('common.selectBranchFirst'))
      return
    }
    if (!form.itemNameEn.trim()) {
      console.warn('[InventoryItemEdit] submit blocked: Name (English) is required')
      toast.error(t('inventoryItems.validationNameEn'))
      return
    }
    if (!form.itemNameAr.trim()) {
      console.warn('[InventoryItemEdit] submit blocked: Name (Arabic) is required')
      toast.error(t('inventoryItems.validationNameAr'))
      return
    }
    if (!form.itemCategoryId) {
      console.warn('[InventoryItemEdit] submit blocked: Category is required')
      toast.error(t('inventoryItems.validationCategory'))
      return
    }

    try {
      setSaving(true)
      const payload: ItemUpsertPayload = {
        ...form,
        itemId: isEditMode ? form.itemId : 0,
        branchId,
        tax: isProcurementType ? PROCUREMENT_PURCHASE_TAX_ID : form.tax,
        sellingTax: isProcurementType ? PROCUREMENT_SELLING_TAX_ID : form.sellingTax,
        isTaxable: isProcurementType ? false : form.isTaxable,
        createdDate: form.createdDate || new Date().toISOString(),
        createdBy: form.createdBy || userId,
        modifiedDate: new Date().toISOString(),
        modifiedBy: userId,
      }
      const fileInfo =
        payload.attachmentFile instanceof File
          ? { name: payload.attachmentFile.name, size: payload.attachmentFile.size, type: payload.attachmentFile.type }
          : payload.attachmentFile
            ? { name: (payload.attachmentFile as any)?.name }
            : null

      console.groupCollapsed('[InventoryItemEdit] save payload')
      console.log('mode', { isEditMode, isProcurementType })
      console.log('payload (raw)', payload)
      console.log('payload (json)', {
        ...payload,
        attachmentFile: fileInfo,
      })
      console.groupEnd()

      if (isEditMode) {
        const res = await itemService.update(payload)
        console.groupCollapsed('[InventoryItemEdit] update response (exact)')
        console.log('status', res?.status)
        console.log('statusText', res?.statusText)
        console.log('headers', res?.headers)
        console.log('data', res?.data)
        console.groupEnd()
      } else {
        const res = await itemService.save(payload)
        console.groupCollapsed('[InventoryItemEdit] save response (exact)')
        console.log('status', res?.status)
        console.log('statusText', res?.statusText)
        console.log('headers', res?.headers)
        console.log('data', res?.data)
        console.groupEnd()
      }

      toast.dismiss()
      await showSaveSuccess(isEditMode)
      navigate('/inventory/items')
    } catch (error: any) {
      console.groupCollapsed('[InventoryItemEdit] save/update error (exact)')
      console.log('message', error?.message)
      console.log('status', error?.response?.status)
      console.log('statusText', error?.response?.statusText)
      console.log('headers', error?.response?.headers)
      console.log('data', error?.response?.data)
      console.groupEnd()
      toast.error(error.response?.data?.message || t('inventoryItems.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const onFileChange = (file: File | null) => {
    if (!file) {
      setForm((prev) => ({ ...prev, attachmentFile: null }))
      return
    }

    const acceptedTypes = ['image/png', 'image/jpeg', 'image/jpg']
    if (!acceptedTypes.includes(file.type)) {
      toast.error(t('inventoryItems.imageTypeError'))
      return
    }

    setForm((prev) => ({ ...prev, attachmentFile: file }))
    const url = URL.createObjectURL(file)
    setImagePreviewUrl(url)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryItems.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryItems.formSubtitle')}</p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader className="border-b pb-4">
          <CardTitle className="text-lg">{t('inventoryItems.sectionItem')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-4">
              <div className="space-y-3 lg:col-span-1">
                <Label>{t('inventoryItems.profileImage')}</Label>
                <button
                  type="button"
                  className="flex h-52 w-full items-center justify-center rounded-lg border bg-muted/20"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={saving || loading}
                >
                  {imagePreviewUrl ? (
                    <img src={imagePreviewUrl} alt="item profile" className="h-full w-full rounded-lg object-contain" />
                  ) : (
                    <span className="text-sm text-muted-foreground">{t('inventoryItems.noImage')}</span>
                  )}
                </button>
                <div>
                  <Input
                    ref={imageInputRef}
                    type="file"
                    className="hidden"
                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                    onChange={(e) => onFileChange(e.target.files?.[0] || null)}
                    disabled={saving || loading}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    <Upload className="mr-1 inline h-3.5 w-3.5" />
                    {t('inventoryItems.imageHint')}
                  </p>
                </div>
              </div>

              <div className="space-y-6 lg:col-span-3">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="inv-name-en">{t('inventoryItems.nameEn')} *</Label>
                    <Input
                      id="inv-name-en"
                      value={form.itemNameEn}
                      onChange={(e) => setForm((p) => ({ ...p, itemNameEn: e.target.value }))}
                      disabled={saving || loading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inv-name-ar">{t('inventoryItems.nameAr')} *</Label>
                    <Input
                      id="inv-name-ar"
                      dir="rtl"
                      className="text-right"
                      value={form.itemNameAr}
                      onChange={(e) => setForm((p) => ({ ...p, itemNameAr: e.target.value }))}
                      disabled={saving || loading}
                    />
                  </div>
                </div>

                {isEditMode ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="inv-code">{t('inventoryItems.itemCode')}</Label>
                      <Input
                        id="inv-code"
                        value={form.itemCode}
                        readOnly
                        className="focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inv-barcode">{t('inventoryItems.barcode')}</Label>
                      <Input
                        id="inv-barcode"
                        value={form.barcode}
                        readOnly
                        className="focus-visible:ring-0 focus-visible:ring-offset-0"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>{t('inventoryItems.category')} *</Label>
                    <SearchableSelect
                      value={String(form.itemCategoryId || 0)}
                      onValueChange={(v) => {
                        const nextCategoryId = Number(v || 0)
                        setForm((p) => ({
                          ...p,
                          itemCategoryId: nextCategoryId,
                          subCategoryId: nextCategoryId === p.itemCategoryId ? p.subCategoryId : 0,
                        }))
                      }}
                      disabled={saving || loading}
                      placeholder={t('inventoryItems.selectCategory')}
                      searchPlaceholder={t('inventoryItems.searchCategory')}
                      emptyMessage={t('inventoryItems.noCategoryFound')}
                      options={[
                        { value: '0', label: t('inventoryItems.selectCategory') },
                        ...categoryOptions.map((x) => ({ value: x.value, label: x.label })),
                      ]}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('inventoryItems.subCategory')}</Label>
                    <Select
                      value={String(form.subCategoryId || 0)}
                      onValueChange={(v) => setForm((p) => ({ ...p, subCategoryId: Number(v || 0) }))}
                      disabled={saving || loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('inventoryItems.selectSubCategory')} />
                      </SelectTrigger>
                      <SelectContent searchable>
                        <SelectItem value="0">{t('inventoryItems.selectSubCategory')}</SelectItem>
                        {subCategoryOptions.map((x) => (
                          <SelectItem key={x.value} value={x.value}>
                            {x.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div
                  className={
                    isProcurementType ? 'grid gap-4 sm:grid-cols-2' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-4'
                  }
                >
                  <div className="space-y-2">
                    <Label>{t('inventoryItems.materialType')}</Label>
                    <Select
                      value={String(form.type || 0)}
                      onValueChange={(v) => setForm((p) => ({ ...p, type: Number(v || 0) }))}
                      disabled={saving || loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('inventoryItems.selectMaterialType')} />
                      </SelectTrigger>
                      <SelectContent searchable>
                        <SelectItem value="0">{t('inventoryItems.selectMaterialType')}</SelectItem>
                        {materialTypeOptions.map((x) => (
                          <SelectItem key={x.value} value={x.value}>
                            {x.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('inventoryItems.uom')}</Label>
                    <Select
                      value={String(form.uom || 0)}
                      onValueChange={(v) => setForm((p) => ({ ...p, uom: Number(v || 0) }))}
                      disabled={saving || loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('inventoryItems.selectUom')} />
                      </SelectTrigger>
                      <SelectContent searchable>
                        <SelectItem value="0">{t('inventoryItems.selectUom')}</SelectItem>
                        {uomSelectOptions.map((x) => (
                          <SelectItem key={x.value} value={x.value}>
                            {x.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {isProcurementType ? (
                    <div className="space-y-2">
                      <Label htmlFor="inv-unit-cost-price">{t('inventoryItems.unitCostPrice')}</Label>
                      <Input
                        id="inv-unit-cost-price"
                        type="number"
                        step="0.01"
                        value={form.unitCostPrice}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            unitCostPrice: Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : 0,
                          }))
                        }
                        disabled={saving || loading}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>{t('inventoryItems.purchaseTax')}</Label>
                        <Select
                          value={String(form.tax ?? 0)}
                          onValueChange={(v) => setForm((p) => ({ ...p, tax: Number(v || 0) }))}
                          disabled={saving || loading}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value="0">—</SelectItem>
                            {purchaseTaxOptions.map((x) => (
                              <SelectItem key={x.value} value={x.value}>
                                {x.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="inv-sales-tax">{t('inventoryItems.salesTax')}</Label>
                        <Select
                          value={String(form.sellingTax ?? 0)}
                          onValueChange={(v) => setForm((p) => ({ ...p, sellingTax: Number(v || 0) }))}
                          disabled={saving || loading}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value="0">—</SelectItem>
                            {salesTaxOptions.map((x) => (
                              <SelectItem key={x.value} value={x.value}>
                                {x.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>

                {!isProcurementType ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="inv-unit-cost-price">{t('inventoryItems.unitCostPrice')}</Label>
                      <Input
                        id="inv-unit-cost-price"
                        type="number"
                        step="0.01"
                        value={form.unitCostPrice}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            unitCostPrice: Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : 0,
                          }))
                        }
                        disabled={saving || loading}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="inv-unit-selling-price">{t('inventoryItems.unitSellingPrice')}</Label>
                      <Input
                        id="inv-unit-selling-price"
                        type="number"
                        step="0.01"
                        value={form.unitSellingPrice}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            unitSellingPrice: Number.isFinite(e.target.valueAsNumber) ? e.target.valueAsNumber : 0,
                          }))
                        }
                        disabled={saving || loading}
                      />
                    </div>
                  </div>
                ) : null}

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="inv-desc">{t('inventoryItems.description')}</Label>
                    <Input
                      id="inv-desc"
                      value={form.description}
                      onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                      disabled={saving || loading}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={handleBack} disabled={saving || loading}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.back')}
              </Button>
              <Button type="submit" disabled={saving || loading}>
                <Save className="mr-2 h-4 w-4" />
                {isEditMode ? t('common.update') : t('inventoryItems.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
